/**
 * Excluir um arquivo do caso na aba Documentos (E216.F4, GUS-317 item 3): `POST
 * /casos/{id}/documentos/{documento}/exclusao` com o motivo e o fundamento. O arquivo sai da análise,
 * o original fica guardado com o mesmo SHA-256 e o histórico do caso registra a exclusão; o servidor
 * prepara o caso de novo. A exclusão não se desfaz: para voltar a usar o arquivo, envia-se de novo.
 */
import axios, { type AxiosInstance } from 'axios';

import { FUNDAMENTO_MINIMO } from '@/lib/correcaoDoRascunho';

export type MotivoDaExclusaoNaMesa = 'repetido' | 'outra_transacao' | 'copia_sem_hash' | 'outro';

/** A ordem em que os motivos aparecem na tela. */
export const MOTIVOS_DA_EXCLUSAO: readonly MotivoDaExclusaoNaMesa[] = [
  'repetido',
  'outra_transacao',
  'copia_sem_hash',
  'outro',
];

/** O mesmo mínimo da edição do exame (`FUNDAMENTO_MINIMO` da API). */
export const FUNDAMENTO_MINIMO_DA_EXCLUSAO = FUNDAMENTO_MINIMO;
export const FUNDAMENTO_MAXIMO_DA_EXCLUSAO = 1000;

export interface PedidoDeExclusao {
  motivo: MotivoDaExclusaoNaMesa;
  fundamento: string;
}

/** Por que a exclusão não foi feita, na língua da tela. */
export type RecusaDaExclusao = 'contido' | 'certificacao' | 'revisao' | 'nao_encontrado' | 'falha';

const POR_DETALHE: Readonly<Record<string, RecusaDaExclusao>> = {
  DOCUMENTO_CONTIDO: 'contido',
  DOCUMENT_HAS_OPEN_CERTIFICATION: 'certificacao',
  REVISAO_DA_SECAO_2: 'revisao',
  DOCUMENT_NOT_FOUND: 'nao_encontrado',
};

export class ExclusaoNaMesaError extends Error {
  constructor(readonly recusa: RecusaDaExclusao) {
    super(recusa);
    this.name = 'ExclusaoNaMesaError';
  }
}

function recusaDoErro(err: unknown): RecusaDaExclusao {
  if (!axios.isAxiosError(err)) return 'falha';
  const detalhe: unknown = (err.response?.data as { detail?: unknown } | undefined)?.detail;
  return typeof detalhe === 'string' ? (POR_DETALHE[detalhe] ?? 'falha') : 'falha';
}

/** O que ainda falta para excluir, ou `null` com o pedido pronto. */
export function faltaParaExcluir(
  motivo: MotivoDaExclusaoNaMesa | null,
  fundamento: string,
): 'motivo' | 'fundamento' | null {
  if (motivo === null) return 'motivo';
  return fundamento.trim().length < FUNDAMENTO_MINIMO_DA_EXCLUSAO ? 'fundamento' : null;
}

/**
 * Owner, 03/11 (GT-2026-0056 da POC): a exclusão grava e responde depois de o caso liberar a
 * escrita; com a preparação do caso em curso, passou dos 15 s do cliente padrão — o arquivo saiu,
 * mas a tela disse "não foi possível excluir". O mesmo teto do processamento inicial.
 */
export const EXCLUSAO_TIMEOUT_MS = 120_000;

export async function excluirNaMesa(
  client: AxiosInstance,
  caseId: string,
  documentoId: string,
  pedido: PedidoDeExclusao,
): Promise<void> {
  try {
    await client.post(
      `/casos/${caseId}/documentos/${documentoId}/exclusao`,
      { motivo: pedido.motivo, fundamento: pedido.fundamento.trim() },
      { timeout: EXCLUSAO_TIMEOUT_MS },
    );
  } catch (err) {
    throw new ExclusaoNaMesaError(recusaDoErro(err));
  }
}
